<script setup lang="ts">
import type { Panel, PanelHandle } from '@/composables/use-panels';
import { X } from 'lucide-vue-next';
import { ref, computed, watch, onUnmounted } from 'vue';

const props = defineProps<{
	panel: Panel;
	handle: PanelHandle;
}>();

defineEmits<{
	close: [];
}>();

// track the "just saved" flash — true for 500ms after saveProgress transitions to null
const justSaved = ref(false);

let savedTimer: ReturnType<typeof setTimeout> | null = null;
watch(
	() => props.handle.saveProgress,
	(next, prev) => {
		if (prev != null && next == null) {
			justSaved.value = true;
			if (savedTimer) clearTimeout(savedTimer);
			savedTimer = setTimeout(() => {
				justSaved.value = false;
				savedTimer = null;
			}, 500);
		}
	},
);

onUnmounted(() => {
	if (savedTimer) clearTimeout(savedTimer);
});

// resolved progress value: loading takes priority over saving
const progress = computed(() => {
	if (props.handle.loadProgress != null) return props.handle.loadProgress;
	if (props.handle.saveProgress != null) return props.handle.saveProgress;
	return null;
});

const isIndeterminate = computed(() => progress.value != null && !Number.isFinite(progress.value));
const isDeterminate = computed(() => progress.value != null && Number.isFinite(progress.value));

// SVG arc for determinate progress (0-100)
const arcPath = computed(() => {
	if (!isDeterminate.value) return '';
	const pct = Math.max(0, Math.min(100, progress.value!));
	if (pct <= 0) return '';
	if (pct >= 100) {
		// full circle
		return 'M 8 1.5 A 6.5 6.5 0 1 1 7.999 1.5';
	}
	const angle = (pct / 100) * 360;
	const rad = (angle - 90) * (Math.PI / 180);
	const x = 8 + 6.5 * Math.cos(rad);
	const y = 8 + 6.5 * Math.sin(rad);
	const large = angle > 180 ? 1 : 0;
	return `M 8 1.5 A 6.5 6.5 0 ${large} 1 ${x} ${y}`;
});

// which visual state to render
const state = computed<'progress' | 'saved' | 'error' | 'dirty' | 'none'>(() => {
	if (progress.value != null) return 'progress';
	if (justSaved.value) return 'saved';
	if (props.handle.error) return 'error';
	if (props.handle.dirty) return 'dirty';
	return 'none';
});
</script>

<template>
	<span class="size-4 group" @mouseup.stop>
		<!-- progress ring (loading or saving) -->
		<svg
			v-if="state === 'progress'"
			class="size-4 shrink-0 group-hover:hidden"
			viewBox="0 0 16 16"
			fill="none"
		>
			<!-- background ring -->
			<circle
				cx="8"
				cy="8"
				r="6.5"
				stroke="currentColor"
				stroke-opacity="0.2"
				stroke-width="1.5"
				fill="none"
			/>

			<!-- determinate arc fill -->
			<path
				v-if="isDeterminate"
				:d="arcPath"
				stroke="currentColor"
				stroke-opacity="0.7"
				stroke-width="1.5"
				fill="none"
				stroke-linecap="round"
			/>

			<!-- indeterminate rotating slice -->
			<path
				v-if="isIndeterminate"
				d="M 8 1.5 A 6.5 6.5 0 0 1 14.1 5.5"
				stroke="currentColor"
				stroke-opacity="0.7"
				stroke-width="1.5"
				fill="none"
				stroke-linecap="round"
				class="animate-spin origin-center"
			/>
		</svg>

		<!-- just saved: green dot -->
		<svg
			v-else-if="state === 'saved'"
			class="size-4 shrink-0 group-hover:hidden"
			viewBox="0 0 16 16"
		>
			<circle cx="8" cy="8" r="6" class="fill-green-500/50" />
		</svg>

		<!-- error: red dot -->
		<svg
			v-else-if="state === 'error'"
			class="size-4 shrink-0 group-hover:hidden"
			viewBox="0 0 16 16"
		>
			<circle cx="8" cy="8" r="6" class="fill-red-500/50" />
		</svg>

		<!-- dirty: current-color dot -->
		<svg
			v-else-if="state === 'dirty'"
			class="size-4 shrink-0 group-hover:hidden"
			viewBox="0 0 16 16"
		>
			<circle cx="8" cy="8" r="6" fill="currentColor" fill-opacity="0.3" />
		</svg>

		<!-- close -->
		<span
			v-if="panel.closable"
			:class="[
				'inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-opacity hover:text-foreground group-hover:opacity-100',
				{
					'not-group-hover:hidden': state !== 'none',
				},
			]"
			title="Close"
			@mousedown.stop
			@click.stop="$emit('close')"
		>
			<X class="size-4" />
		</span>
	</span>
</template>
